import * as Sentry from '@sentry/react';
import type { Event, EventHint } from '@sentry/react';

import { isDevelopment } from '../environment';

const DOM_REMOVE_CHILD_MESSAGE =
  "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.";

const toSearchableStrings = (event: Event, hint?: EventHint): string[] => {
  const exceptionStrings = (event.exception?.values ?? []).flatMap((exception) => [
    exception.type,
    exception.value,
  ]);

  const originalException = hint?.originalException;
  const originalExceptionStrings =
    originalException instanceof Error
      ? [originalException.name, originalException.message]
      : [String(originalException ?? '')];

  return [...exceptionStrings, ...originalExceptionStrings].filter(Boolean) as string[];
};

export const isDomRemoveChildNoise = (event: Event, hint?: EventHint): boolean => {
  return toSearchableStrings(event, hint).some((value) =>
    value.toLowerCase().includes(DOM_REMOVE_CHILD_MESSAGE.toLowerCase())
  );
};

export const initSentry = () => {
  if (isDevelopment) {
    return;
  }

  Sentry.init({
    dsn: process.env.REACT_APP_SETNRY_DNS,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    environment: process.env.REACT_APP_ENV,
    ignoreErrors: [
      /Failed to fetch/i,
      /AxiosError/,
      /No window\.opener\.\s*Can't complete notification\./i,
      /No window\.opener\.\s*Can't complete auth/i,
      /unreachable\. Response did not contain/i,
      /ErrorResponse: Unauthenticated/i,
    ],
    beforeSend: (event, hint) => {
      // Browser extensions and page translators can mutate React-managed DOM nodes,
      // which makes React throw this NotFoundError while removing a child node. It is
      // client-side noise and not an app bug we can recover from.
      if (isDomRemoveChildNoise(event, hint)) {
        return null;
      }

      return event;
    },
  });
};
